import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Church as ChurchIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface Parohie {
  id: string;
  nume: string;
  adresa: string;
  oras: string;
  provincia: string;
  regione: string;
  codPostal: string;
  telefon: string;
  email: string;
  paroh: string;
  parohId?: string;
  vicar?: string;
  vicarId?: string;
  secretar?: string;
  secretarId?: string;
  anInfiintare: number;
  numarCredinciosi: number;
  status: 'activ' | 'inactiv' | 'in_constructie';
  descriere?: string;
  imagine?: string;
  gestiune: {
    contBancar: string;
    banca: string;
    soldInitial: number;
    soldCurent: number;
    responsabilGestiune: string;
    responsabilId?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface PersonalParohie {
  id: string;
  nume: string;
  functie: 'paroh' | 'vicar' | 'secretar' | 'cantor' | 'psalt' | 'alt';
  parohieId: string;
  telefon?: string;
  email?: string;
  dataAngajare: string;
  salariu?: number;
  isActive: boolean;
}

const Parohii: React.FC = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRegione, setFilterRegione] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedParohie, setSelectedParohie] = useState<Parohie | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Mock data pentru parohii românești din Italia
  const [parohii] = useState<Parohie[]>([
    {
      id: 'parohia1',
      nume: 'Parohia Sf. Nicolae',
      adresa: 'Via Mazzini 202',
      oras: 'Porretta Terme',
      provincia: 'Bologna',
      regione: 'Emilia-Romagna',
      codPostal: '40046',
      telefon: '+39 0534 22123',
      email: 'porretta@dor-italia.it',
      paroh: 'Părintele Gheorghe Zavate',
      parohId: 'user3',
      vicar: 'Părintele Mihai Popescu',
      vicarId: 'user6',
      secretar: 'Anna Rossi',
      secretarId: 'user7',
      anInfiintare: 2010,
      numarCredinciosi: 350,
      status: 'activ',
      descriere: 'Parohie ortodoxă română înființată în 2010 pentru a servi comunitatea română din Porretta Terme',
      gestiune: {
        contBancar: 'IT60X0542811101000000123456',
        banca: 'Banca Popolare di Sondrio',
        soldInitial: 5000,
        soldCurent: 8500,
        responsabilGestiune: 'Anna Rossi',
        responsabilId: 'user7'
      },
      createdAt: '2023-01-01',
      updatedAt: '2024-01-15'
    },
    {
      id: 'parohia2',
      nume: 'Parohia Sf. Gheorghe',
      adresa: 'Via Roma 45',
      oras: 'Milano',
      provincia: 'Milano',
      regione: 'Lombardia',
      codPostal: '20121',
      telefon: '+39 02 1234567',
      email: 'milano@dor-italia.it',
      paroh: 'Părintele Vasile Ionescu',
      parohId: 'user4',
      secretar: 'Maria Bianchi',
      secretarId: 'user8',
      anInfiintare: 1995,
      numarCredinciosi: 1200,
      status: 'activ',
      descriere: 'Parohie istorică din centrul Milanului, punct de referință pentru comunitatea ortodoxă română',
      gestiune: {
        contBancar: 'IT40S0300203280100000001234',
        banca: 'UniCredit',
        soldInitial: 15000,
        soldCurent: 22000,
        responsabilGestiune: 'Părintele Vasile Ionescu',
        responsabilId: 'user4'
      },
      createdAt: '2023-01-15',
      updatedAt: '2024-01-10'
    },
    {
      id: 'parohia3',
      nume: 'Parohia Sf. Maria',
      adresa: 'Via Garibaldi 78',
      oras: 'Roma',
      provincia: 'Roma',
      regione: 'Lazio',
      codPostal: '00100',
      telefon: '+39 06 9876543',
      email: 'roma@dor-italia.it',
      paroh: 'Părintele Ion Popescu',
      parohId: 'user5',
      anInfiintare: 2020,
      numarCredinciosi: 800,
      status: 'in_constructie',
      descriere: 'Parohie nouă în construcție pentru a servi comunitatea ortodoxă română în creștere din Roma',
      gestiune: {
        contBancar: 'IT89M0300203280100000005678',
        banca: 'Intesa Sanpaolo',
        soldInitial: 8000,
        soldCurent: 12000,
        responsabilGestiune: 'Părintele Ion Popescu',
        responsabilId: 'user5'
      },
      createdAt: '2023-02-01',
      updatedAt: '2024-01-05'
    },
    {
      id: 'parohia4',
      nume: 'Parohia Sf. Spiridion',
      adresa: 'Corso Italia 123',
      oras: 'Torino',
      provincia: 'Torino',
      regione: 'Piemonte',
      codPostal: '10100',
      telefon: '+39 011 2345678',
      email: 'torino@dor-italia.it',
      paroh: 'Părintele Alexandru Marin',
      parohId: 'user9',
      secretar: 'Giulia Ferrari',
      secretarId: 'user10',
      anInfiintare: 2005,
      numarCredinciosi: 650,
      status: 'activ',
      descriere: 'Parohie activă în inima Torino-ului, centru spiritual pentru comunitatea ortodoxă română',
      gestiune: {
        contBancar: 'IT17X0300203280100000009012',
        banca: 'Banca Sella',
        soldInitial: 10000,
        soldCurent: 18000,
        responsabilGestiune: 'Giulia Ferrari',
        responsabilId: 'user10'
      },
      createdAt: '2023-03-01',
      updatedAt: '2024-01-20'
    }
  ]);

  // Mock data pentru personalul parohiilor
  const [personalParohii] = useState<PersonalParohie[]>([
    {
      id: 'pers1',
      nume: 'Părintele Gheorghe Zavate',
      functie: 'paroh',
      parohieId: 'parohia1',
      telefon: '+39 349 135 33 43',
      email: 'giozavate@hotmail.com',
      dataAngajare: '2010-01-01',
      salariu: 2500,
      isActive: true
    },
    {
      id: 'pers2',
      nume: 'Părintele Mihai Popescu',
      functie: 'vicar',
      parohieId: 'parohia1',
      telefon: '+39 349 135 33 44',
      email: 'mihai@dor-italia.it',
      dataAngajare: '2021-06-01',
      salariu: 2000,
      isActive: true
    },
    {
      id: 'pers3',
      nume: 'Anna Rossi',
      functie: 'secretar',
      parohieId: 'parohia1',
      telefon: '+39 349 135 33 45',
      email: 'anna.rossi@dor-italia.it',
      dataAngajare: '2019-03-01',
      salariu: 1800,
      isActive: true
    },
    {
      id: 'pers4',
      nume: 'Părintele Vasile Ionescu',
      functie: 'paroh',
      parohieId: 'parohia2',
      telefon: '+39 02 1234567',
      email: 'vasile@dor-italia.it',
      dataAngajare: '1995-09-01',
      salariu: 2800,
      isActive: true
    },
    {
      id: 'pers5',
      nume: 'Maria Bianchi',
      functie: 'secretar',
      parohieId: 'parohia2',
      telefon: '+39 02 1234568',
      email: 'maria.bianchi@dor-italia.it',
      dataAngajare: '2018-01-15',
      salariu: 1900,
      isActive: true
    }
  ]);

  const regioni = ['Lombardia', 'Lazio', 'Emilia-Romagna', 'Piemonte', 'Toscana', 'Veneto', 'Campania', 'Puglia', 'Sicilia', 'Sardegna'];
  const statusOptions = ['activ', 'inactiv', 'in_constructie'];

  const filteredParohii = parohii.filter(parohie => {
    const matchesSearch = parohie.nume.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         parohie.oras.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         parohie.paroh.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || parohie.status === filterStatus;
    const matchesRegione = !filterRegione || parohie.regione === filterRegione;
    
    return matchesSearch && matchesStatus && matchesRegione;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'activ': return 'success';
      case 'inactiv': return 'error';
      case 'in_constructie': return 'warning';
      default: return 'default';
    }
  };

  const getFunctieColor = (functie: string) => {
    switch (functie) {
      case 'paroh': return 'error';
      case 'vicar': return 'warning';
      case 'secretar': return 'info';
      case 'cantor': return 'primary';
      case 'psalt': return 'secondary';
      default: return 'default';
    }
  };

  const handleViewParohie = (parohie: Parohie) => {
    setSelectedParohie(parohie);
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleEditParohie = (parohie: Parohie) => {
    setSelectedParohie(parohie);
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedParohie(null);
    setIsEditing(false);
  };

  const handleDeleteParohie = (parohieId: string) => {
    // Implementare ștergere parohie
    console.log('Ștergere parohie:', parohieId);
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  const totalParohii = parohii.length;
  const parohiiActive = parohii.filter(p => p.status === 'activ').length;
  const parohiiInConstructie = parohii.filter(p => p.status === 'in_constructie').length;
  const totalCredinciosi = parohii.reduce((sum, p) => sum + p.numarCredinciosi, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Parohii
        </Typography>
        {hasPermission('parohii.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare parohie nouă */}}
          >
            Parohie nouă
          </Button>
        )}
      </Box>

      {/* Statistici */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ChurchIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Total parohii
                </Typography>
              </Box>
              <Typography variant="h5">
                {totalParohii}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AdminIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Parohii active
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {parohiiActive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationIcon color="warning" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  În construcție
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {parohiiInConstructie}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon color="info" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Total credincioși
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                {totalCredinciosi.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Toate parohiile" />
          <Tab label="Parohii active" />
          <Tab label="În construcție" />
          <Tab label="Personal" />
        </Tabs>

        {/* Tab Toate parohiile */}
        <TabPanel value={activeTab} index={0}>
          {/* Filtre */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Caută parohii..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {statusOptions.map(status => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Regiune</InputLabel>
                  <Select
                    value={filterRegione}
                    onChange={(e) => setFilterRegione(e.target.value)}
                    label="Regiune"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {regioni.map(regione => (
                      <MenuItem key={regione} value={regione}>{regione}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('');
                    setFilterRegione('');
                  }}
                >
                  Resetează
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabel parohii */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Parohie</TableCell>
                  <TableCell>Locație</TableCell>
                  <TableCell>Paroh</TableCell>
                  <TableCell>Credincioși</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>An înființare</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredParohii.map((parohie) => (
                  <TableRow key={parohie.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          <ChurchIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {parohie.nume}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {parohie.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {parohie.oras}, {parohie.provincia}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {parohie.adresa}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {parohie.paroh}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {parohie.numarCredinciosi.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={parohie.status.replace('_', ' ')}
                        color={getStatusColor(parohie.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{parohie.anInfiintare}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewParohie(parohie)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {hasPermission('parohii.write') && (
                        <>
                          <IconButton
                            onClick={() => handleEditParohie(parohie)}
                            color="secondary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteParohie(parohie.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab Parohii active */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Parohii active
          </Typography>
          <Grid container spacing={2}>
            {parohii.filter(p => p.status === 'activ').map((parohie) => (
              <Grid item xs={12} md={6} lg={4} key={parohie.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'success.main' }}>
                        <ChurchIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{parohie.nume}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {parohie.oras}, {parohie.provincia}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Paroh:</strong> {parohie.paroh}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Credincioși:</strong> {parohie.numarCredinciosi.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Telefon:</strong> {parohie.telefon}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong> {parohie.email}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Tab În construcție */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Parohii în construcție
          </Typography>
          <List>
            {parohii.filter(p => p.status === 'in_constructie').map((parohie) => (
              <React.Fragment key={parohie.id}>
                <ListItem>
                  <ListItemIcon>
                    <LocationIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={parohie.nume}
                    secondary={`${parohie.oras}, ${parohie.provincia} • Paroh: ${parohie.paroh} • An înființare: ${parohie.anInfiintare}`}
                  />
                  <Chip
                    label="În construcție"
                    color="warning"
                    size="small"
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        {/* Tab Personal */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Personalul parohiilor
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nume</TableCell>
                  <TableCell>Funcție</TableCell>
                  <TableCell>Parohie</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Data angajare</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {personalParohii.map((persoana) => {
                  const parohie = parohii.find(p => p.id === persoana.parohieId);
                  return (
                    <TableRow key={persoana.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {persoana.nume}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={persoana.functie}
                          color={getFunctieColor(persoana.functie) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{parohie?.nume || '-'}</TableCell>
                      <TableCell>{persoana.telefon || '-'}</TableCell>
                      <TableCell>{persoana.email || '-'}</TableCell>
                      <TableCell>{persoana.dataAngajare}</TableCell>
                      <TableCell>
                        <Chip
                          label={persoana.isActive ? 'Activ' : 'Inactiv'}
                          color={persoana.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Dialog pentru vizualizare/editare parohie */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isEditing ? 'Editare parohie' : 'Detalii parohie'} - {selectedParohie?.nume}
        </DialogTitle>
        <DialogContent>
          {selectedParohie && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nume parohie"
                  value={selectedParohie.nume}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="An înființare"
                  type="number"
                  value={selectedParohie.anInfiintare}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Adresa"
                  value={selectedParohie.adresa}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Oraș"
                  value={selectedParohie.oras}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Provincia"
                  value={selectedParohie.provincia}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cod poștal"
                  value={selectedParohie.codPostal}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={selectedParohie.telefon}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={selectedParohie.email}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Număr credincioși"
                  type="number"
                  value={selectedParohie.numarCredinciosi}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedParohie.status}
                    label="Status"
                  >
                    {statusOptions.map(status => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descriere"
                  multiline
                  rows={3}
                  value={selectedParohie.descriere || ''}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Personal
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Paroh"
                      value={selectedParohie.paroh}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Vicar"
                      value={selectedParohie.vicar || ''}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Secretar"
                      value={selectedParohie.secretar || ''}
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Informații Gestiune
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cont bancar"
                      value={selectedParohie.gestiune.contBancar}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bancă"
                      value={selectedParohie.gestiune.banca}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sold curent"
                      type="number"
                      value={selectedParohie.gestiune.soldCurent}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Responsabil gestiune"
                      value={selectedParohie.gestiune.responsabilGestiune}
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {isEditing ? 'Anulează' : 'Închide'}
          </Button>
          {isEditing && (
            <Button variant="contained">
              Salvează modificările
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Parohii;
